import { useEffect, useState } from "react";
import { Label, makeStyles, tokens, Text, Card, CardHeader, Caption1 } from "@fluentui/react-components";
import { IStorageInOneDriveReportSummaryProps } from "./storage-in-onedrive-report-summary.props";
import PercentageBar from "components/common/percentage-bar/percentage-bar";
import { getSizeLabel } from "lib/utils";
import { useL10n } from "context/l10n-context";
import { ILocaleStrings } from "loc/locale.interfaces";
import LinkCell from "components/reports-common/report-results-grid-cells/link-cell/link-cell";

const layoutStyles = makeStyles({
    section: {
        display: 'flex',
        flexDirection: 'column',
        marginBottom: '1em'
    },
    ownerCard: {
        marginBottom: '20px',
        maxWidth: '500px',
        backgroundColor: tokens.colorNeutralBackground1,
        border: `1px solid ${tokens.colorNeutralStroke2}`,
        borderRadius: tokens.borderRadiusMedium
    },
    oneDriveImage: {
        width: '32px',
        height: '32px'
    },
    textWrapper: {
        overflow: 'hidden',
        display: 'inline-grid',
        '> span': {
            display: 'block',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
        }
    }
});

export default function StorageInOneDriveReportSummary(props: IStorageInOneDriveReportSummaryProps) {
    const styles = layoutStyles();
    const { t } = useL10n<ILocaleStrings>();

    const [additionalLabel, setAdditionalLabel] = useState<string>("");
    const [additionalWarningLabel, setAdditionalWarningLabel] = useState<string>("");

    useEffect(() => {
        if (props.report?.details) {
            const used = getSizeLabel(props.report.details.siteTotalSizeKB, 'KB');
            const quota = getSizeLabel(props.report.details.siteQuotaMaximumMB, 'MB');
            setAdditionalLabel(`(${t("ppaQuotaUsedValue", { used: used, quota: quota })})`);

            const warningLevel = getSizeLabel(props.report.details.storageWarningLevelMB, 'MB');
            const maxLevel = getSizeLabel(props.report.details.storageMaximumLevelMB, 'MB');
            setAdditionalWarningLabel(`(${t("ppaWarningLevelValue", { warning: warningLevel, max: maxLevel })})`);
        }
    }, [props.report?.details]);

    return (<div>
        {props.report?.details &&
            <>
                <Card className={styles.ownerCard}>
                    <CardHeader
                        image={<img className={styles.oneDriveImage} src="images/OneDriveLogo.svg" alt={t("ppaOneDrive")} />}
                        header={
                            <div className={styles.textWrapper}>
                                <Text as="span" weight="semibold">
                                    <LinkCell label={props.report.details.ownerName} url={props.report.details.oneDriveUrl} />
                                </Text>
                            </div>
                        }
                        description={
                            <div className={styles.textWrapper}>
                                <Caption1 as="span">{props.report.details.ownerEmail}</Caption1>
                            </div>
                        }
                    />
                </Card>

                <div className={styles.section}>
                    <Label>{t("ppaPercentageOfQuota")}</Label>
                    <PercentageBar
                        value={props.report.details.percentageOfQuota}
                        valueDecimalPlaces={2}
                        showLabel={true}
                        additionalLabel={additionalLabel} />
                </div>
                <div className={styles.section}>
                    <Label>{t("ppaStorageLimits")}</Label>
                    <div>{props.report.details.isQuotaSiteSpecific ? t("ppaStorageQuotaSiteSpecificDescription") : t("ppaStorageQuotaTenantWideDescription")}</div>
                </div>
                {props.report.details.isQuotaSiteSpecific &&
                    <>
                        <Label>{t("ppaWarningLevel")}</Label>
                        <PercentageBar
                            value={props.report.details.warningLevelPercentage}
                            valueColour={tokens.colorNeutralStroke1}
                            spareColour={tokens.colorStatusWarningBackground3}
                            showLabel={true}
                            additionalLabel={additionalWarningLabel} />
                    </>
                }
            </>}
    </div>);
}